import cx from "classnames";
import { ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useUser } from "../../../../contexts/UserContext";
import { useSetUserRegistration, useUserRegistration } from "../../../../contexts/UserRegistrationContext";
import { useLocalStorageToggle, useToggle } from "../../../../hooks/common";
import {
  AccountantRoutes,
  AdminRoutes,
  Level1Routes,
  Level2Routes,
  Level3Routes,
  NavigationRoute,
  UserRoutes,
} from "./Sidebar.const";
import { cookieUtils } from "../../../../utils/cookieUtils";
import { useVisibleSidebarRoutes } from "./visibleSidebarRoutes";

function DropdownNavigationLink({
  icon,
  label,
  toggle,
  open,
  svg,
  children,
}: {
  children?: ReactNode[];
  svg?: string;
  toggle: () => unknown;
  open?: boolean;
  icon?: string;
  label: string;
}) {
  const [show, toggleContent] = useToggle();
  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (!show && !open) toggle();
          toggleContent();
        }}
        className={
          "flex px-2 md:px-4 py-2 w-full items-center justify-center space-x-4 text-sm md:text-base"
        }
      >
        {!!icon && (
          <i
            className={`fa w-full shrink-0 max-w-[28px] text-3xl !m-0 fa-${icon} text-inherit`}
          ></i>
        )}
        {!!svg && (
          <img
            src={svg}
            className={`shrink-0 min-w-[28px] w-[28px] h-[32px]`}
          ></img>
        )}
        {open && (
          <span
            title={label}
            className="grow text-nowrap text-ellipsis overflow-hidden whitespace-nowrap text-left"
          >
            {label}
          </span>
        )}
        {open && (
          <i
            className={cx(
              "fa !m-0 fa-angle-down transition text-2xl text-inherit shrink-0",
              { ["rotate-180"]: show }
            )}
          ></i>
        )}
      </button>
      {show && open && <div className="pl-8 space-y-1">{children}</div>}
    </>
  );
}

function NavigationLink({
  open,
  icon,
  label,
  link,
  size = "xl",
  svg,
}: {
  open?: boolean;
  icon?: string;
  svg?: string;
  label: string;
  link: string;
  size?: "xl" | "sm";
}) {
  return (
    <NavLink
      to={link}
      className={({ isActive }) =>
        cx(
          `flex w-full text-sm md:text-base hover:text-inherit hover:bg-primary rounded items-center justify-center space-x-4 ${
            isActive ? "bg-primary" : ""
          }`,
          {
            ["py-0.5 px-2"]: size === "sm",
            ["py-2 px-2 md:px-4"]: size === "xl",
          }
        )
      }
    >
      {!!icon && (
        <i
          title={label}
          className={cx(`text-center fa w-full !m-0 fa-${icon} text-inherit`, {
            ["max-w-[28px] text-3xl"]: size === "xl",
            ["max-w-[24px] text-sm"]: size === "sm",
          })}
        ></i>
      )}
      {!!svg && (
        <img
          alt={label}
          title={label}
          src={svg}
          className={`shrink-0 min-w-[28px] w-[28px] h-[32px]`}
        ></img>
      )}
      {open && (
        <span
          title={label}
          className="grow text-nowrap text-ellipsis overflow-hidden whitespace-nowrap"
        >
          {label}
        </span>
      )}
    </NavLink>
  );
}

function SideBar() {
  const [open, toggleSidebar] = useLocalStorageToggle("sidebar");
  const user = useUser();
  console.log("user information" , user);
  const userRegistration =   cookieUtils.getSecureCookie('userRegistrationDetails');
  ;
  // const setUserRegistration = useSetUserRegistration();
 
    // setUserRegistration(userRegistration);
  console.log("user registration information" , userRegistration);
  // const [isAdminMode, setIsAdminMode] = useState<boolean>(
  //   localStorage.getItem("isAdminMode") === "false" ? false : true
  // );
  const [isAdminMode, setIsAdminMode] = useState<boolean>(() => {
    const adminMode = cookieUtils.getSecureCookie('isAdminMode');
    return adminMode === "false" ? false : true;
  });

  const inStorage = cookieUtils.getSecureCookie('isAdminMode');

  // const inStorage = localStorage.getItem("isAdminMode");

  useEffect(() => {
    setIsAdminMode(inStorage === "false" ? false : true);
    console.log("setIsAdminMode instorage is hit", inStorage);
  }, [inStorage]);

  let configToUse: NavigationRoute[] = [];
  if ((user?.role || "").toLowerCase() === "accountant") {
    configToUse = AccountantRoutes;
  } else if (
    ["user", "architect"].includes(user?.role?.toLowerCase() || "") 
    &&
    userRegistration?.regestration_status === 1
  ) {
    configToUse = UserRoutes;
  } else if (user?.role === "Level 1") {
    configToUse = Level1Routes;
  } else if (user?.role === "Level 2") {
    configToUse = Level2Routes;
  } else if (user?.role === "Level 3") {
    configToUse = Level3Routes;
  } else if (user?.role === "System Admin" && !isAdminMode) {
    configToUse = Level3Routes;
  } else if (user?.role === "System Admin" && isAdminMode) {
    configToUse = AdminRoutes;
  }

  /** A reviewer level's list loses the entries the Admin has switched off for it. */
  configToUse = useVisibleSidebarRoutes(configToUse);

  if (!configToUse.length) return null;

  return (
    <div
      className={cx(
        "p-4 h-full overflow-auto box-border border-r-2 border-gray-200 bg-white shrink-0",
        {
          ["w-[300px]"]: open,
        }
      )}
    >
      <button
        type="button"
        onClick={toggleSidebar}
        className={
          "mb-4 flex px-4 py-2 w-full rounded items-center justify-center uppercase font-semibold space-x-4"
        }
      >
        <i
          className={
            "fa w-full max-w-[28px] text-2xl !m-0 fa-align-left text-inherit"
          }
        ></i>
        {open && <span className="grow text-left">Menu</span>}
      </button>
      <nav className="space-y-3">
        {configToUse.map((config) => {
          if (config.type === "dropdown") {
            const { options, ...rest } = config;
            return (
              <DropdownNavigationLink
                {...rest}
                open={open}
                toggle={toggleSidebar}
              >
                {options.map((e) => (
                  <NavigationLink open={open} {...e} size="sm" key={e.link} />
                ))}
              </DropdownNavigationLink>
            );
          }
          return <NavigationLink open={open} {...config} key={config.link} />;
        })}
      </nav>
    </div>
  );
}

export function MobileSideBar({
  onClose,
  open,
}: {
  open: boolean;
  onClose: () => unknown;
}) {
  const user = useUser();
  const url = useLocation().pathname;
  const userRegistration = useUserRegistration();
  // const [isAdminMode, setIsAdminMode] = useState<boolean>(
  //   localStorage.getItem("isAdminMode") === "true" ? true : false
  // );
  const [isAdminMode, setIsAdminMode] = useState<boolean>(() => {
    const adminMode = cookieUtils.getSecureCookie('isAdminMode');
    return adminMode === "true" ? true : false;
  });

  useEffect(() => {
    if (open)
      return () => {
        onClose();
      };
    return () => {};
  }, [url, open]);

  // const inStorage = localStorage.getItem("isAdminMode");
  const inStorage = cookieUtils.getSecureCookie('isAdminMode');


  useEffect(() => {
    setIsAdminMode(inStorage === "true" ? true : false);
  }, [inStorage]);

  let configToUse: NavigationRoute[] = [];

  if ((user?.role || "").toLowerCase() === "accountant") {
    configToUse = AccountantRoutes;
  } else if (
    ["user", "architect"].includes(user?.role?.toLowerCase() || "") &&
    userRegistration?.regestration_status === 1
  ) {
    configToUse = UserRoutes;
  } else if (user?.role === "Level 1") {
    configToUse = Level1Routes;
  } else if (user?.role === "Level 2") {
    configToUse = Level2Routes;
  } else if (
    (user?.role === "Level 3" && !isAdminMode) ||
    (user?.role === "System Admin" && !isAdminMode)
  ) {
    configToUse = Level3Routes;
  } else if (
    (user?.role === "Level 3" && isAdminMode) ||
    (user?.role === "System Admin" && isAdminMode)
  ) {
    configToUse = AdminRoutes;
  }

  /** A reviewer level's list loses the entries the Admin has switched off for it. */
  configToUse = useVisibleSidebarRoutes(configToUse);

  if (!configToUse.length || !open) return null;

  return (
    <>
      <div
        className="absolute z-[9999] bg-[rgba(0,_0,_0,_0.7)] bottom-0 right-0 left-0 top-0"
        onClick={onClose}
      ></div>
      <div
        className={cx(
          "p-4 h-full overflow-auto box-border border-r-2 border-gray-200 w-[80%] absolute bg-white z-[10000]"
        )}
      >
        <nav className="space-y-1">
          {configToUse.map((config) => {
            if (config.type === "dropdown") {
              const { options, ...rest } = config;
              return (
                <DropdownNavigationLink {...rest} open toggle={() => null}>
                  {options.map((e) => (
                    <NavigationLink open {...e} size="sm" key={e.link} />
                  ))}
                </DropdownNavigationLink>
              );
            }
            return <NavigationLink open {...config} key={config.link} />;
          })}
        </nav>
      </div>
    </>
  );
}

export default SideBar;
